import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CameraChannelType } from "@app/main/components/videos/enums/CameraChannelType";
import { CameraLocation } from "@app/main/components/videos/enums/CameraLocation";
import { ICameraChannel } from "@app/main/components/videos/models/ICameraChannel";
import { CameraChannelService, IChannelThumbnail } from "@app/main/modules/cameras/_services/camera-channels.service";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError, finalize, map, take } from "rxjs/operators";

import { IVehicle } from "@app/interfaces/IVehicle";
import { FoundationVehiclesService } from "@app/main/modules/foundation/_services/vehicle.service";
import { IVehicleWithCameraMappings } from "../camera-vehicles-manager/camera-vehicles-manager.component";
import './camera-channels-editor.component.scss';

export interface ICameraChannelWithThumbnail extends ICameraChannel {
    thumbnailUrl?: string;
}

interface ILoadDataResponse {
    channels?: ICameraChannel[];
    thumbnails?: IChannelThumbnail[];
    vehicle?: IVehicle;
}

@Component({
    selector: 'camera-channels-editor',
    template: require('./camera-channels-editor.component.html')
})
export class CameraChannelsEditorComponent implements OnChanges {

    @Input() vehicle: IVehicleWithCameraMappings;
    @Input() vehicleId: number;

    @Output() save = new EventEmitter<IVehicleWithCameraMappings>();

    //--

    cameraChannels: ICameraChannelWithThumbnail[] = [];
    locations = Object.keys(CameraLocation).filter(s => !isNaN(Number(s))).map(s => Number(s));
    types = Object.keys(CameraChannelType).filter(s => !isNaN(Number(s))).map(s => Number(s));

    CameraLocation = CameraLocation;
    CameraChannelType = CameraChannelType;

    loading: boolean;

    subscriptions = new Subscription();

    constructor(
        private readonly cameraChannelService: CameraChannelService,
        private readonly vehicleService: FoundationVehiclesService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["vehicle"] || changes["vehicleId"]) {
            this.processVehicleChange();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    saveChannels(): void {
        this.vehicle.cameraChannels = this.cameraChannels;
        this.cameraChannelService.updateCameraChannels(this.cameraChannels).pipe(take(1)).subscribe(() => {
            this.save.emit(this.vehicle);
        });
    }

    //--

    private async processVehicleChange(): Promise<void> {
        if (this.vehicle) {
            this.vehicleId = this.vehicle?.Id;
        }

        if (!this.vehicleId) {
            return;
        }

        this.loadData();
    }

    private loadData(): void {

        this.loading = true;

        const cameraChannelsObservable = this.vehicle?.cameraChannels?.length
            ? of(this.vehicle.cameraChannels)
            : this.cameraChannelService.getCameraChannels({ VehicleId: this.vehicleId });

        const vehicleObservable = this.vehicle
            ? of(this.vehicle)
            : this.vehicleService.getVehicle(this.vehicleId)

        this.subscriptions.add(forkJoin({

            channels: cameraChannelsObservable,
            thumbnails: this.cameraChannelService.getChannelThumbnails(this.vehicleId),
            vehicle: vehicleObservable

        }).pipe(
            map((data: ILoadDataResponse) => this.processData(data)),
            catchError((error) => {
                this.loading = false;
                throw error;
            }),
            finalize(() => this.loading = false)
        ).subscribe());
    }

    private processData(data: ILoadDataResponse): void {

        this.cameraChannels = data.channels;
        this.vehicle = data.vehicle;

        if (!data.thumbnails.length || !data.channels?.length) {
            return;
        }

        data.channels.forEach((channel: ICameraChannelWithThumbnail) => {
            const thumbnailForChannel: IChannelThumbnail = data.thumbnails.find(thumbnail => thumbnail.ChannelId === channel.ChannelId);

            if (!thumbnailForChannel?.Guid) {
                return;
            }

            channel.thumbnailUrl = this.cameraChannelService.createMediaGuidUrl(thumbnailForChannel.Guid);
        });

        data.channels.sort((channel1, channel2) => channel1.ChannelId - channel2.ChannelId) as ICameraChannelWithThumbnail[];
    }
}
